import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { CircularProgress } from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
  Cell,
} from "recharts";

const BAR_COLOR = "#0066CC";
const GRID_COLOR = "#E6EAF2";

const formatNumber = (value) => {
  const num = Number(value || 0);

  return num.toLocaleString("en-IN", {
    minimumFractionDigits: Number.isInteger(num) ? 0 : 2,
    maximumFractionDigits: 2,
  });
};

const CustomYAxisTick = ({ x, y, payload }) => {
  const value = payload?.value || "";

  const lines = value
    .replace("Aggregation Flag Not Selected", "Aggregation Flag Not\nSelected")
    .split("\n");

  return (
    <g transform={`translate(${x},${y})`}>
      {lines.map((line, index) => (
        <text
          key={`${line}-${index}`}
          x={0}
          y={(index - (lines.length - 1) / 2) * 16}
          textAnchor="end"
          fill="#59718A"
          fontSize={12}
          fontFamily="Unilever Shilling"
        >
          {line}
        </text>
      ))}
    </g>
  );
};

CustomYAxisTick.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  payload: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};

CustomYAxisTick.defaultProps = {
  x: 0,
  y: 0,
  payload: {},
};

const CustomBarLabel = (props) => {
  const { x, y, width, height, index, value, data } = props;

  const currentRow = data?.[index] || {};

  const percent = Number(currentRow.percent || 0);
  const total = Number(value || 0);

  const label = `${formatNumber(total.toFixed(2))} (${percent.toFixed(2)}%)`;

  return (
    <text
      x={x + width + 10}
      y={y + height / 2 + 4}
      textAnchor="start"
      fill="#1A2025"
      fontSize={12}
      fontWeight={600}
      fontFamily="Unilever Shilling"
    >
      {label}
    </text>
  );
};

CustomBarLabel.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  width: PropTypes.number,
  height: PropTypes.number,
  index: PropTypes.number,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  data: PropTypes.arrayOf(PropTypes.shape({})),
};

CustomBarLabel.defaultProps = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  index: 0,
  value: 0,
  data: [],
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const item = payload[0].payload;

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #D9DEEB",
        borderRadius: "8px",
        padding: "10px",
        boxShadow: "0px 2px 8px rgba(0,0,0,0.12)",
      }}
    >
      <div
        style={{
          fontWeight: 600,
          marginBottom: 6,
          color: "#0066CC",
          fontSize: "12px",
        }}
      >
        {item.stamping}
      </div>

      <div style={{ fontSize: "12px" }}>Total: {formatNumber(item.total)}</div>

      <div style={{ fontSize: "12px" }}>
        Percent: {item.percent.toFixed(2)}%
      </div>
    </div>
  );
};

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(
    PropTypes.shape({
      payload: PropTypes.shape({
        stamping: PropTypes.string,
        total: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        percent: PropTypes.number,
      }),
    }),
  ),
};

CustomTooltip.defaultProps = {
  active: false,
  payload: [],
};

const CustomBarLabelWithData = React.memo(({ data, ...props }) => (
  <CustomBarLabel {...props} data={data} />
));

CustomBarLabelWithData.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})),
};

CustomBarLabelWithData.defaultProps = {
  data: [],
};

const BucketsOverviewGraph = ({ data, loading }) => {
  const chartData = useMemo(() => {
    const rows = (data || []).map((item) => ({
      stamping: item?.stamping || "",
      total: Number(item?.["stamping total"] || 0),
      percent: Number(item?.["stamping percent"] || 0),
    }));

    return rows.sort((a, b) => b.total - a.total);
  }, [data]);

  const maxValue = useMemo(() => {
    const highest = Math.max(...chartData.map((item) => item.total), 0);

    if (highest <= 0) {
      return 100;
    }

    const rawMax = highest * 1.5;

    return Math.ceil(rawMax / 100) * 100;
  }, [chartData]);

  const ticks = useMemo(() => {
    const interval = Math.ceil(maxValue / 5);

    return [
      0,
      interval,
      interval * 2,
      interval * 3,
      interval * 4,
      interval * 5,
    ];
  }, [maxValue]);

  if (loading) {
    return (
      <div
        style={{
          height: 260,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  if (!chartData.length) {
    return (
      <div
        style={{
          height: 260,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#6B7280",
        }}
      >
        No data available
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        height: "260px",
        overflow: "hidden",
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          layout="vertical"
          data={chartData}
          margin={{
            top: 10,
            right: 60,
            left: 10,
            bottom: 10,
          }}
          barCategoryGap="28%"
        >
          <CartesianGrid stroke={GRID_COLOR} horizontal={false} vertical />

          <XAxis
            type="number"
            domain={[0, maxValue]}
            ticks={ticks}
            allowDecimals={false}
            axisLine={false}
            tickLine={false}
            tick={{
              fill: "#59718A",
              fontSize: 12,
            }}
          />

          <YAxis
            type="category"
            dataKey="stamping"
            width={140}
            axisLine={false}
            tickLine={false}
            interval={0}
            tick={<CustomYAxisTick />}
          />

          <Tooltip content={<CustomTooltip />} />

          <Bar dataKey="total" radius={[0, 8, 8, 0]} isAnimationActive={false}>
            {chartData.map((row) => (
              <Cell key={row.stamping} fill={BAR_COLOR} />
            ))}

            <LabelList
              dataKey="total"
              content={<CustomBarLabelWithData data={chartData} />}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

BucketsOverviewGraph.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})),
  loading: PropTypes.bool,
};

BucketsOverviewGraph.defaultProps = {
  data: [],
  loading: false,
};

export default BucketsOverviewGraph;
